import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import {
  IVidro,
  IVidroCor,
  ISelectOption,
  VidroTabIndex,
} from "./types/vidro.types";
import {
  obterVidroPorId,
  salvarVidro,
  excluirVidro,
  duplicarVidro,
  listarReferencias,
  listarLinhas,
  listarTiposVidro,
  listarCoresVidro,
  listarArredondamentos,
  obterCoresVidro,
  salvarCoresVidro,
  validarCodigoUnico,
} from "./services/vidroService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";
import { TabGeral } from "./components/TabGeral";
import { TabCores } from "./components/TabCores";
import { ModalGerenciamentoMidias } from "./components/ModalGerenciamentoMidias";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela do Vidro (enum TipoTela = 3).
const ID_TIPO_TELA_VIDRO = 3;

// Helper para formatar data de atualização
const formatarDataAtualizacao = (data?: string) => {
  if (!data) return "";
  try {
    const date = new Date(data);
    return `${date.toLocaleDateString("pt-BR")} - ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch (e) {
    console.error("Erro ao formatar data:", e);
    return "";
  }
};

export const CadastroVidro: React.FC = () => {
  const navigate = useNavigate();
  const { id: idVidroParam } = useParams<{ id: string }>();
  const idVidro = idVidroParam ? parseInt(idVidroParam) : undefined;
  const isEdicao = !!idVidro;

  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" = somente-leitura.
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de vidros em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("vidros");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  const [tabIndex, setTabIndex] = useState<VidroTabIndex>(0);
  const [vidro, setVidro] = useState<Partial<IVidro>>({
    codigo: "",
    descricao: "",
    // FASE 05 — 6ª validação/PO 2026-08-03: cadastro novo já nasce com Situação e Status
    // válidos (o backend exige >= 1 via [Range]). Sem isto o payload ia com 0 e o Criar
    // retornava 400 "Campo IdStatus/IdSituacao não pode ser negativo" (paridade com Componente).
    idSituacao: 1, // Em edição
    idStatus: 1, // Ativo
    idTipoVidro: undefined,
    espessura: 0,
    peso: 0,
    fatorLH: 0,
    controleSolar: undefined,
    bloqueioRuido: undefined,
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: default de cadastro novo = "P/CIMA" (id 1).
    idArredondamento: 1,
  });

  // Cores separadas para melhor controle
  const [cores, setCores] = useState<IVidroCor[]>([]);

  // Listas de referência
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);
  const [linhas, setLinhas] = useState<ISelectOption[]>([]);
  const [tiposVidro, setTiposVidro] = useState<ISelectOption[]>([]);
  const [coresLista, setCoresLista] = useState<ISelectOption[]>([]);
  const [arredondamentos, setArredondamentos] = useState<ISelectOption[]>([]);

  // Modais
  const [modalMidiasAberto, setModalMidiasAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // FASE 05 — S3/PO 2026-05-13: carrega listas globais. Referencias filtrada por idTipoTela=3.
  // Linhas e carregada via useEffect quando idReferencia muda.
  const carregarListasReferencia = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        listarReferencias(ID_TIPO_TELA_VIDRO),
        listarTiposVidro(),
        listarCoresVidro(),
        listarArredondamentos(),
      ]);

      const [refsResult, tiposResult, coresResult, arredondamentosResult] = results;

      const refsData = refsResult.status === 'fulfilled' ? refsResult.value : [];
      const tiposData = tiposResult.status === 'fulfilled' ? tiposResult.value : [];
      const coresData = coresResult.status === 'fulfilled' ? coresResult.value : [];
      const arredondamentosData = arredondamentosResult.status === 'fulfilled' ? arredondamentosResult.value : [];

      setReferencias(refsData);
      setTiposVidro(tiposData);
      setCoresLista(coresData);
      setArredondamentos(arredondamentosData);
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
    }
  }, []);

  // Carregar dados do vidro (se edição)
  const carregarVidro = useCallback(async () => {
    if (!idVidro) {
      setLoading(false);
      return;
    }

    try {
      const vidroData = await obterVidroPorId(idVidro);
      
      if (vidroData) {
        setVidro(vidroData);
        // Carregar cores do vidro
        if (vidroData.cores && vidroData.cores.length > 0) {
          setCores(vidroData.cores);
        } else {
          const coresData = await obterCoresVidro(idVidro);
          setCores(coresData || []);
        }
      } else {
        openNotification({ message: "Vidro não encontrado", type: "error" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar vidro:", error);
      openNotification({ message: "Erro ao carregar vidro", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idVidro, navigate, openNotification]);

  useEffect(() => {
    carregarListasReferencia();
    carregarVidro();
  }, [carregarListasReferencia, carregarVidro]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado } = useValidacaoCodigoUnico(
    vidro.codigo,
    idVidro,
    validarCodigoUnico
  );

  // FASE 05 — S3/PO 2026-05-13: cascata Referencia -> Linha.
  useEffect(() => {
    if (vidro.idReferencia) {
      listarLinhas(vidro.idReferencia).then(setLinhas);
    } else {
      setLinhas([]);
    }
  }, [vidro.idReferencia]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue as VidroTabIndex);
  };

  // FASE 05 — S3/PO 2026-05-13: limpar Linha ao trocar Referencia.
  const handleVidroChange = (campo: keyof IVidro, valor: any) => {
    setVidro((prev) => {
      const next: Partial<IVidro> = { ...prev, [campo]: valor };
      if (campo === "idReferencia") {
        next.idLinha = undefined;
      }
      return next;
    });
  };

  // Recarrega o cadastro após salvar, e o hook rearma o retrato sozinho — daí só `alterado`.
  // Plano 21/09/2026 (Frente 1): a aba Cores é estado separado de `vidro` — fora do retrato,
  // mexer só nela deixava SALVAR desabilitado e a alteração se perdia (reporte de 21/09).
  const { alterado } = useFormularioAlterado(
    { vidro, cores },
    { ehNovo: !isEdicao, pronto: !loading }
  );

  const handleSalvar = async () => {
    // Validações
    if (!vidro.codigo?.trim()) {
      openNotification({ message: "Código é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.descricao?.trim()) {
      openNotification({ message: "Descrição é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem longa padronizada (padrão do Componente).
    if (codigoDuplicado) {
      openNotification({
        message: "Este código de produto já existe. Informe um código diferente ou inclua o sufixo da sua empresa ou entre em contato com o departamento de Produto Informação da Alumisoft.",
        type: "warning",
      });
      setTabIndex(0);
      return;
    }

    // FASE 05 — 4ª validação/Frente 9 2026-07-30 (P2): Referência e Linha são obrigatórias.
    if (!vidro.idReferencia) {
      openNotification({ message: "Referência é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.idLinha) {
      openNotification({ message: "Linha é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    if (!vidro.idTipoVidro) {
      openNotification({ message: "Tipo é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.espessura || vidro.espessura <= 0) {
      openNotification({ message: "Espessura é obrigatória e deve ser maior que zero", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.peso || vidro.peso <= 0) {
      openNotification({ message: "Peso é obrigatório e deve ser maior que zero", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.fatorLH || vidro.fatorLH <= 0) {
      openNotification({ message: "Fator L/H é obrigatório e deve ser maior que zero", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!vidro.idArredondamento) {
      openNotification({ message: "Arredondamento é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (cores.length > 0) {
      // Validar cores: não permitir duplicatas
      const coresDuplicadas = cores.filter((c, index) => 
        cores.findIndex((c2) => c2.idCor === c.idCor && c2.ordem !== c.ordem) !== -1
      );
      if (coresDuplicadas.length > 0) {
        openNotification({ message: "Não é permitido cores duplicadas", type: "warning" });
        setTabIndex(1);
        return;
      }
      // Validar que todas as cores têm idCor
      const coresSemCor = cores.filter((c) => !c.idCor || c.idCor === 0);
      if (coresSemCor.length > 0) {
        openNotification({ message: "Todas as cores devem ter uma cor selecionada", type: "warning" });
        setTabIndex(1);
        return;
      }
    }

    setSaving(true);
    try {
      const dadosParaSalvar: Partial<IVidro> = {
        ...vidro,
      };

      const resultado = await salvarVidro(dadosParaSalvar);
      if (resultado?.sucesso) {
        openNotification({ message: "Vidro salvo com sucesso", type: "success" });
        
        // Se era novo cadastro, redireciona para edição
        if (!isEdicao && resultado.id) {
          navigate(`/produtosN1/vidros/${resultado.id}`, { replace: true });
          // Salvar cores após o vidro ser criado
          if (cores.length > 0) {
            await salvarCoresVidro(resultado.id, cores);
          }
        } else if (isEdicao && idVidro && resultado.id) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): as cores vão para o id RETORNADO —
          // o salvar pode ter congelado a versão de mercado e criado uma nova cópia de trabalho.
          await salvarCoresVidro(resultado.id, cores);
          if (String(resultado.id) !== String(idVidro)) {
            navigate(`/produtosN1/vidros/${resultado.id}`, { replace: true });
          } else {
            carregarVidro(); // Recarrega os dados
          }
        }
      } else {
        // FASE 05 — 4ª validação/D5 2026-07-30: o servidor recusou (400/500) e a tela ficava
        // muda — a falha passa a ser exibida com a mensagem real da API.
        openNotification({
          message: resultado?.mensagem || "Erro ao salvar vidro. Verifique os campos obrigatórios e tente novamente.",
          type: "error",
        });
      }
    } catch (error: any) {
      console.error("Erro ao salvar:", error);
      openNotification({
        message: error?.message || "Erro ao salvar vidro. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idVidro) return;

    setSaving(true);
    try {
      const resultado = await duplicarVidro(idVidro);
      if (resultado?.sucesso && resultado.id) {
        openNotification({ message: "Vidro duplicado com sucesso", type: "success" });
        navigate(`/produtosN1/vidros/${resultado.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Erro ao duplicar:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (!idVidro) return;

    setSaving(true);
    try {
      const sucesso = await excluirVidro(idVidro);
      if (sucesso) {
        openNotification({ message: "Vidro excluído com sucesso", type: "success" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleMidiasSave = (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
  }, idAlvo?: number) => {
    setVidro((prev) => ({
      ...prev,
      imagemUrl: midias.imagemUrl,
      imagemNomeArquivo: midias.imagemNomeArquivo,
    }));
    // Plano 21/09/2026 (Frente 3): vidro Disponível → a mídia foi para a cópia de trabalho;
    // navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
    if (idAlvo && String(idAlvo) !== String(idVidro)) {
      navigate(`/produtosN1/vidros/${idAlvo}`, { replace: true });
    }
  };

  const handleVoltar = () => {
    navigate("/produtosN1");
  };

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0: // GERAL
        return (
          <TabGeral
            vidro={vidro}
            onChange={handleVidroChange}
            referencias={referencias}
            linhas={linhas}
            tiposVidro={tiposVidro}
            arredondamentos={arredondamentos}
            codigoDuplicado={codigoDuplicado}
            onOpenMidias={() => setModalMidiasAberto(true)}
          />
        );
      case 1: // CORES
        return (
          <TabCores
            cores={cores}
            onChange={setCores}
            coresLista={coresLista}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Vidro
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Vidros
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Novo Vidro"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
          
          {/* Última atualização e Log */}
          {isEdicao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
              {vidro.dataUltimaAtualizacao && formatarDataAtualizacao(vidro.dataUltimaAtualizacao) && (
                <Typography variant="body2" color="text.secondary">
                  Última atualização: {formatarDataAtualizacao(vidro.dataUltimaAtualizacao)}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={() => setModalHistoricoAberto(true)}
                sx={{ color: "#3C90AD" }}
              >
                Log de atualizações
              </Link>
            </Box>
          )}

          <AvisoPlanilhaPendenteN1 tipoMaterial="vidros" />

          {/* Abas */}
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              mb: 3,
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: "2px",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: "52px",
                backgroundColor: "#7ab8cc",
                color: "#fff",
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                },
              },
            }}
          >
            <Tab label="GERAL" />
            <Tab label="CORES" />
          </Tabs>

          {/* Conteúdo das Abas — bloqueado quando somente-leitura (B4). */}
          <Box
            sx={{
              flexGrow: 1,
              pb: 4,
              ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }),
            }}
          >
            {renderTabContent()}
          </Box>
        </StackCustom>

        {/* Rodapé com botões de ação — oculto em somente-leitura (B4). */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      {/* Modais */}
      <ModalGerenciamentoMidias
        open={modalMidiasAberto}
        onClose={() => setModalMidiasAberto(false)}
        vidro={vidro}
        onSave={handleMidiasSave}
        onRefreshVidro={carregarVidro}
      />
      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        codigo={vidro.codigo || ""}
        descricao={vidro.descricao || ""}
      />
      <ModalHistorico
        open={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        vidroId={idVidro}
        vidroCodigo={vidro.codigo || ""}
      />
    </DashboardStackCustom>
  );
};
